/**
 * 字典文案国际化
 * - 根据接口返回的 typeId、id，从当前语言的 JSON 中取展示文案
 * - 支持 zh-CN、en-US，后续可扩展其他语言
 */
import zhCN from './dictionaries/zh-CN.json';
import enUS from './dictionaries/en-US.json';

const dictionaries = {
  'zh-CN': zhCN,
  'en-US': enUS,
};

const FALLBACK_LOCALE = 'zh-CN';

/** 将运行时的 i18n.locale 规范到与 dictionaries 一致的 key（如 en → en-US） */
export function normalizeDictionaryLocale(locale) {
  if (!locale || typeof locale !== 'string') return FALLBACK_LOCALE;
  if (dictionaries[locale]) return locale;
  const lower = locale.toLowerCase().replace('_', '-');
  if (lower === 'en' || lower === 'enus' || lower === 'en-us') return 'en-US';
  if (lower.startsWith('en-')) return 'en-US';
  if (lower === 'zh' || lower === 'zh-cn') return 'zh-CN';
  if (lower.startsWith('zh-')) return 'zh-CN';
  return FALLBACK_LOCALE;
}

/**
 * 根据字典类型、项 id 和语言，返回对应文案
 * @param {string|number} typeId - 字典大类 ID（如 6158、6003）
 * @param {string|number} id - 字典项 ID（如 6158001）
 * @param {string} [locale] - 语言，如 'zh-CN' | 'en-US'；不传则用 fallback
 * @returns {string} 文案，找不到时返回空字符串或 id 的字符串形式
 */
export function getDictionaryName(typeId, id, locale = FALLBACK_LOCALE) {
  const t = String(typeId);
  const i = String(id);
  const loc = normalizeDictionaryLocale(locale);
  const dict = dictionaries[loc] || dictionaries[FALLBACK_LOCALE];
  const byType = dict && dict[t];
  const name = byType && byType[i];
  return name != null ? name : '';
}

/**
 * 将接口返回的字典列表转为带当前语言 name 的选项（保留 id，name 用字典文案）
 * @param {string|number} typeId - 字典大类 ID
 * @param {Array<{id: string|number, name?: string}>} list - getDictionaryList 返回的 data
 * @param {string} locale - 当前语言
 * @returns {Array<{id, name, key?, value?}>} 项含 id、name（已国际化），以及 key/value 便于下拉使用
 */
export function mapDictionaryListToOptions(typeId, list, locale = FALLBACK_LOCALE) {
  if (!Array.isArray(list)) return [];
  return list.map((item) => {
    const name = getDictionaryName(typeId, item.id, locale);
    return {
      ...item,
      name: name || item.name || String(item.id),
      key: item.id,
      value: name || item.name || String(item.id),
    };
  });
}

/**
 * `getRecursionDictionaryList` 二级（或多级）树：节点 `label` 用 dictionaries 文案，无则回退接口 `name`。
 * 输出与 MaintenanceProcessing / el-cascader 的 `id`、`name`、`childList` 一致。
 * @param {string|number} typeId - 字典大类 ID（如 7003）
 * @param {unknown} rawData - 接口 res.data
 * @param {string} [locale]
 * @returns {Array<Record<string, unknown>>}
 */
export function mapRecursionDictionaryTreeForDisplay(typeId, rawData, locale = FALLBACK_LOCALE) {
  const list = Array.isArray(rawData) ? rawData : [];
  const loc = normalizeDictionaryLocale(locale);
  return mapRecursionDictionaryTreeNodes(typeId, list, loc);
}

function mapRecursionDictionaryTreeNodes(typeId, nodes, loc) {
  if (!Array.isArray(nodes)) return [];
  const t = Number(typeId);
  return nodes.map((node) => {
    const mapped = getDictionaryName(t, node.id, loc);
    const next = {
      ...node,
      name: mapped || node.name || String(node.id),
    };
    const children = node.childList;
    if (Array.isArray(children) && children.length > 0) {
      next.childList = mapRecursionDictionaryTreeNodes(typeId, children, loc);
    } else {
      delete next.childList;
    }
    return next;
  });
}

/**
 * `queryDictionarySalesListByParent` 三级故障树：节点带 `type`（如 7777 分组、6076 标准字典项），
 * `label` 使用与各 `type` 对应的 dictionaries 文案；无翻译时回退接口 `name`。
 * 叶子节点去掉空的 `childList`，与 el-cascader `children: 'childList'` 一致。
 * @param {unknown} rawData - 接口 res.data
 * @param {string} [locale]
 * @returns {Array<Record<string, unknown>>}
 */
export function transformFaultClassificationSalesTreeForDisplay(rawData, locale = FALLBACK_LOCALE) {
  const list = Array.isArray(rawData) ? rawData : [];
  const loc = normalizeDictionaryLocale(locale);
  let clone;
  try {
    clone = JSON.parse(JSON.stringify(list));
  } catch {
    clone = list;
  }
  return mapFaultClassificationSalesTreeNodes(clone, loc);
}

function mapFaultClassificationSalesTreeNodes(nodes, loc) {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((node) => {
    const typeId =
      node.type != null && node.type !== '' ? Number(node.type) : 6076;
    const mapped = getDictionaryName(typeId, node.id, loc);
    const next = { ...node, name: mapped || node.name || String(node.id) };
    const children = node.childList;
    if (Array.isArray(children) && children.length > 0) {
      next.childList = mapFaultClassificationSalesTreeNodes(children, loc);
    } else {
      delete next.childList;
    }
    return next;
  });
}

export default getDictionaryName;
